<script setup lang="ts">
  import IconQC from '@qicgroupdoha/qic-connect-ds/components/IconQC'
  import ButtonQC from '@qicgroupdoha/qic-connect-ds/components/ButtonQC'
  import WrapperQC from '@qicgroupdoha/qic-connect-ds/components/WrapperQC'
  import TooltipQC from '@qicgroupdoha/qic-connect-ds/components/TooltipQC'
  import { type Plan, SchemeType } from '@/shared/types/api.types'
  import { computed, inject } from 'vue'
  import { PLAN_NAMES_SCHEME_TYPE_CODE_MAP_V3 } from '@/shared/constants/plans.constants'
  import { useI18n } from 'vue-i18n'
  import {
    PLAN_DESCRIPTION_SCHEME_MAPPING_V3,
    WORLDWIDE_EXCL_VARIANTS,
  } from '../../FlowSelectPlanWidget.constants'
  import { useQuoteStore, useUserStore } from '@/shared/store'
  import CoinsDiscountInfo from '@/entities/Flow/components/FlowPlan/components/CoinsDiscountInfo/CoinsDiscountInfo.vue'
  import { useDiscount } from '@/shared/composables'
  import { toFixedNumber } from '@/shared/utils'

  interface Props {
    plan: Plan
    plansSchemeTypeCodes: SchemeType[]
  }

  interface Emits {
    (e: 'select-plan', plan: Plan): void
  }

  interface Slots {
    'after-content'(): void
  }

  const props = defineProps<Props>()
  const emit = defineEmits<Emits>()
  defineSlots<Slots>()

  const { t } = useI18n()
  const quoteStore = useQuoteStore()
  const userStore = useUserStore()

  const schemeTypeCode = computed(() => props.plan.schemeTypeCode)
  const isRTL = inject('isRTL')
  const travelersCount = computed(() => userStore.adultsCount + userStore.childrenCount)

  const discountScheme = computed(() => ({
    discounts: props.plan.discounts,
    price: props.plan.price,
  }))

  const { discount } = useDiscount(discountScheme)

  const quotePriceWithoutDiscount = computed(() =>
    toFixedNumber(props.plan?.price + discount.value.amount)
  )

  const hasType = (type: SchemeType) => props.plansSchemeTypeCodes.includes(type)

  const isRecommended = computed(() => {
    if (schemeTypeCode.value === SchemeType.Worldwide) return true
    return schemeTypeCode.value === SchemeType.WorldwideExcl && !hasType(SchemeType.Worldwide)
  })

  const isValidForVisa = computed(() =>
    [SchemeType.WorldwideExcl, SchemeType.FlyEurope].every(hasType)
  )

  const title = computed(() => {
    let key = ''

    if (schemeTypeCode.value === SchemeType.WorldwideExcl) {
      if (hasType(SchemeType.Worldwide)) {
        key = PLAN_NAMES_SCHEME_TYPE_CODE_MAP_V3[SchemeType.WorldwideExcl]
      } else {
        key = PLAN_NAMES_SCHEME_TYPE_CODE_MAP_V3[SchemeType.Worldwide] ?? ''
      }
    } else {
      key = PLAN_NAMES_SCHEME_TYPE_CODE_MAP_V3[schemeTypeCode.value] ?? ''
    }

    return t(key ?? '')
  })

  const planValues = computed(() => {
    if (schemeTypeCode.value === SchemeType.WorldwideExcl) {
      const variant = WORLDWIDE_EXCL_VARIANTS.find(hasType)
      return variant && PLAN_DESCRIPTION_SCHEME_MAPPING_V3[`${SchemeType.WorldwideExcl}:${variant}`]
    }

    return PLAN_DESCRIPTION_SCHEME_MAPPING_V3[schemeTypeCode.value]
  })

  const handleSelectPlan = () => {
    emit('select-plan', props.plan)
  }
</script>

<template>
  <div class="plan-card" :class="{ recommended: isRecommended }">
    <div class="plan-card__badge-list">
      <div v-if="isValidForVisa" class="badge text-small-medium">
        {{ $t('outbound.select_plan.insurance_values.visa_valid') }}
      </div>
      <div v-if="quoteStore.productBonus" class="badge text-small-medium">
        {{ $t('select_plan.coins', { value: quoteStore.productBonus }) }}
        <!-- qic-connect-ignore-next-line -->
        <img src="@/shared/assets/images/coin.svg" alt="coin" />
      </div>
    </div>

    <div class="plan-card-content">
      <WrapperQC
        direction="column"
        gap="var(--spacing-2)"
        :margin="{ bottom: 'var(--spacing-5)' }"
        class="plan-card-content__header"
      >
        <h2 class="plan-card__title heading-6">
          {{ title }}
        </h2>
        <div class="heading-3 plan-card__price">
          {{ $t('qar_price', { price: plan.price }) }}
          <div v-if="travelersCount > 1" class="text-regular plan-card__travelers">
            /
            {{
              t(
                'outbound.select_plan.insurance_values.travelers',
                { travelersCount },
                travelersCount
              )
            }}
          </div>
        </div>

        <CoinsDiscountInfo
          v-if="plan.priceWithCoinsDiscount"
          :discount-price="Number(plan.priceWithCoinsDiscount)"
        />
        <div v-if="discount.amount > 0" class="text-small-regular plan-card__price-discount">
          {{ $t('qar_price', { price: quotePriceWithoutDiscount }) }}
        </div>
      </WrapperQC>
      <ul class="plan-card__values-list">
        <li v-for="value in planValues" :key="value.title" class="plan-card__value text-regular">
          <IconQC
            v-if="value.excluded"
            name="close-circle-fill"
            color="var(--colorIcnSrfWarningYellow)"
            size="md"
          />
          <IconQC v-else name="check-circle-fill" color="var(--colorIcnSrfAction)" size="md" />
          <div class="plan-card__value-inner">
            {{ $t(value.title) }}
            <template v-if="value.tooltip">
              <IconQC
                v-if="typeof value.tooltip === 'string'"
                v-tooltip-qc="{
                  title: $t(value.tooltip),
                  position: 'bottom',
                  maxWidth: 210,
                }"
                name="info"
                color="var(--colorIcnSrfTertiary)"
                size="md"
              />
              <TooltipQC v-else :max-width="230" position="bottom">
                <template #trigger>
                  <IconQC name="info" color="var(--colorIcnSrfTertiary)" size="md" />
                </template>
                <ul class="tooltip-list-content text-small-medium" :dir="isRTL ? 'rtl' : 'ltr'">
                  <li v-for="(text, textIndex) in value.tooltip" :key="textIndex">
                    {{ $t(text) }}
                  </li>
                </ul>
              </TooltipQC>
            </template>
          </div>
        </li>
      </ul>

      <ButtonQC
        :variant="isRecommended ? 'primary' : 'secondary'"
        wide
        class="plan-card__select-button"
        @click="handleSelectPlan"
      >
        {{ $t('travel.outbound.select_plan.select_button') }}
      </ButtonQC>

      <slot name="after-content" />
    </div>
  </div>
</template>

<style scoped lang="scss">
  .plan-card {
    width: 100%;
    position: relative;
    border: 2px solid var(--colorBgSrfTertiary);
    border-radius: var(--spacing-8);
    display: flex;
    align-items: center;
    justify-content: center;

    &.recommended {
      border: none;
      background: linear-gradient(
        107deg,
        var(--colorAccentPurple700, #6568f4) 0%,
        var(--colorAccentPurple400, #c5ceff) 100%
      );

      &::after {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: var(--spacing-8);
        border: 2px solid transparent;
        background: linear-gradient(
            107deg,
            var(--colorAccentPurple700, #6568f4) 0%,
            var(--colorAccentPurple400, #c5ceff) 100%
          )
          border-box;
        mask:
          linear-gradient(#fff 0 0) padding-box,
          linear-gradient(#fff 0 0);
        mask-composite: exclude;
        pointer-events: none;
        z-index: 1;
      }

      .plan-card__title {
        background: linear-gradient(
          135deg,
          var(--colorAccentPurple700) 0%,
          var(--colorTxtSrfAction) 100%
        );
        background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      .plan-card-content {
        width: calc(100% - 4px);
        height: calc(100% - 4px);
      }

      .plan-card__badge-list {
        top: 2px;
        inset-inline-end: 2px;
      }
    }

    .plan-card__badge-list {
      position: absolute;
      display: flex;
      border-end-start-radius: var(--spacing-4);
      top: 0;
      inset-inline-end: 0;

      .badge {
        display: flex;
        gap: var(--spacing-2);
        align-items: center;
        justify-content: flex-start;
        padding-inline: var(--spacing-4) var(--spacing-6);
        height: 30px;
        color: var(--colorTxtSrfBrandPurple);
        background: var(--colorBgSrfTertiary);
        border-block-end: 2px solid var(--colorBgSrfPrimary);
        border-end-start-radius: var(--spacing-4);

        &:not(:first-child) {
          margin-inline-start: calc(-1 * var(--spacing-4));
          border-inline-start: 2px solid var(--colorBgSrfPrimary);
        }

        &:last-child {
          border-start-end-radius: var(--spacing-8);
        }
      }
    }

    .plan-card__title {
      color: var(--colorTxtSrfSecondary);
    }

    .plan-card-content {
      background-color: var(--colorBgSrfPrimary);

      /* qic-connect-ignore-next-line */
      border-radius: 30px;
      display: flex;
      flex-direction: column;
      padding: var(--spacing-8);
      padding-inline-end: var(--spacing-7);
      height: 100%;
      width: 100%;

      .plan-card-content__header {
        align-items: flex-start;
      }

      /* stylelint-disable-next-line */
      @media (max-width: 768px) {
        padding: var(--spacing-7) var(--spacing-6);
      }
    }

    .plan-card__price {
      color: var(--colorTxtSrfPrimary);
      display: flex;
      align-items: center;
      justify-content: center;
      gap: var(--spacing-2);
    }

    .plan-card__travelers {
      color: var(--colorTxtSrfSecondary);
    }

    .plan-card__value {
      display: flex;
      gap: var(--spacing-2-5);
      padding-block: var(--spacing-1);
      align-items: start;
    }

    .plan-card__values-list {
      margin-bottom: var(--spacing-7);
    }

    .plan-card__price-discount {
      color: var(--colorTxtSrfSecondary);
      text-decoration-line: line-through;
    }
  }

  .flow-select-plan-widget__plans {
    gap: var(--spacing-8) var(--spacing-5);
  }

  .tooltip-list-content {
    text-align: start;
    list-style-type: disc;
    padding-inline-start: var(--spacing-4);
    color: var(--colorTxtSrfPrimary);

    li {
      padding-bottom: var(--spacing-2);
    }
  }

  .plan-card__select-button {
    margin-top: auto;
  }

  .plan-card__value-inner {
    & svg {
      vertical-align: text-bottom;
    }
  }
</style>
